import { trellisPalette } from "components/custom/analytics/palettes";
import getMetricSpec, { type MetricConfig } from "../metrics";
import KeywordCell from "components/custom/category/advertisements/KeywordCell";
import { METRIC_GROUPING_LOOKUP } from "../tables/metricsByEntity";
import type { Column, SortFunction } from "react-table-6";
import type { ReactNode } from "react";
import { type FormatOptions } from "utils/formatNumber";

export interface MetricColumnOptions {
  editCell?: false;
  hideFilter?: boolean;
  filterType?: string;
  selectOptions?: { value: string; label: string }[];
  metric?: Partial<MetricConfig>;
  compare?: Partial<MetricConfig>;
  width?: number;
  overrideCompare?: unknown;
  overrideCompareValue?: unknown;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  overrideCompareFormatter?: (value: any) => ReactNode;
  columnTitle?: string;
  checked?: boolean;
  maxWidth?: number;
  minWidth?: number;
  isStatic?: boolean;
  format?: "number" | "currency" | "percent" | "date" | "string" | "text";
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formatter?: (value: any) => ReactNode;
  toolTip?: string;
  className?: string;
  overflowCell?: boolean;
  showBlank?: boolean;
  showDot?: boolean;
  overrideCompareAccessor?: (row: Record<string, unknown>) => unknown;
  isNegative?: boolean;
  noColor?: boolean;
  cellContainerClasses?: string;
  cellCustomClasses?: (id: string | undefined, value: unknown, row: Record<string, unknown>) => string;
  additionalStyles?: React.CSSProperties;
  height?: number;
  sortMethod?: SortFunction;
}

export interface MetricColumn extends Column {
  checked?: boolean;
  isStatic?: boolean;
  format?: string;
  height?: number;
  toolTip?: string;
  selectOptions?: { value: string; label: string }[];
  filterType?: string;
  grouping?: string;
  accessorId?: string;
  filtered?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  prev_accessor?: (row: any) => unknown;
  isPrev?: boolean;
  name?: string;
  color?: string;
  label?: string;
  clickable?: boolean;
  overrideValue?: number | null;
  actual?: number | null;
  target?: number | null;
  formatter?: (value: unknown) => ReactNode;
  targetFormatter?: (value: unknown) => ReactNode;
  lower?: boolean;
  targetLabel?: string;
  compact?: boolean;
  decimalPlaces?: number;
  compareFormatOptions?: FormatOptions;
  overrideMarketplace?: string;
}

const buildMetricColumn = (
  marketPlace: { marketPlace?: string },
  id: string,
  compareId: string,
  options?: MetricColumnOptions,
  hideCompareValue?: boolean,
): MetricColumn => {
  const { filterType, selectOptions = [] } = options ?? {};
  const metric = getMetricSpec(marketPlace, id, options?.metric);
  const compareSpec = getMetricSpec(marketPlace, compareId, options?.compare);

  // Below looks a bit funny, but allows overrides metric definitions to be shaped the same with all option params being processed
  if (options?.metric) {
    options = { ...options, ...options.metric.options };
  }

  let compare = compareSpec.format ? compareSpec : null;

  const accessor = metric.accessor || ((d: Record<string, unknown>) => d[id]);
  const accessorCmp = compare ? compare.accessor || ((d: Record<string, unknown>) => d[compareId]) : null;

  // Pending accept compare stats from sources outside of data report to match styles
  if (options?.overrideCompare && options.overrideCompareFormatter) {
    const { overrideCompareValue, overrideCompareFormatter } = options;
    compare = {
      formatter: overrideCompareFormatter,
      value: overrideCompareValue,
    };
  }

  const defaultSortMethod = (a: unknown, b: unknown) => {
    if (a === null || a === undefined) {
      return metric.sortNullsLast ? 1 : -1;
    }
    if (b === null || b === undefined) {
      return metric.sortNullsLast ? -1 : 1;
    }
    return (
      a < b ? -1
      : a > b ? 1
      : 0
    );
  };

  return {
    Header: options?.columnTitle || metric.name,
    id,
    accessor,
    sortMethod:
      options?.sortMethod && typeof options.sortMethod === "function" ? options.sortMethod : defaultSortMethod,
    checked: options?.checked ?? true,
    maxWidth: options?.maxWidth,
    minWidth: options?.minWidth,
    isStatic: options?.isStatic,
    format: options?.format ? options.format : metric.format,
    width: options?.width,
    height: options?.height ?? 30,
    toolTip: metric.toolTip ?? options?.toolTip,
    className: options?.className ?? "text-end",
    headerClassName: options?.className ?? "text-end",
    selectOptions: selectOptions,
    filterType: filterType,
    grouping: METRIC_GROUPING_LOOKUP[metric.id ?? id],
    accessorId: metric.accessorId,
    Cell: (props) => {
      const value = accessor(props.row._original);
      if (props.row._original?.isStatsLoading && value === undefined) {
        return (
          <>
            <div
              style={{
                height: "1.3rem",
                maxWidth: "50px",
                marginLeft: "auto",
                marginRight: "5px",
                backgroundColor: "#ddd",
                borderRadius: "0.5rem",
              }}
            />
            {compare && (
              <div
                style={{
                  height: "1.3rem",
                  maxWidth: "40px",
                  marginTop: "0.5rem",
                  marginLeft: "auto",
                  marginRight: "5px",
                  backgroundColor: "#ddd",
                  borderRadius: "0.5rem",
                }}
              />
            )}
          </>
        );
      }
      const fmt = options?.formatter ? options.formatter : metric.formatter;

      if (options?.overflowCell) {
        return <KeywordCell value={fmt(value) as string} />;
      }
      if (options?.showBlank) {
        return (
          <div>
            {fmt(value)}
            <br />
            <span
              style={{
                fontSize: "1.2rem",
                fontWeight: "bold",
              }}
            >
              -
            </span>
          </div>
        );
      }

      if ((options?.showDot ?? true) && compare && !hideCompareValue) {
        const valueCmp =
          options?.overrideCompare ?
            typeof options.overrideCompareAccessor === "function" ?
              options.overrideCompareAccessor(props.row._original)
            : options.overrideCompare
          : accessorCmp ? (accessorCmp(props.row._original) ?? "")
          : "";
        const fmtCmp = compare.formatter;
        const isNegative = options?.isNegative ?? metric.lower ?? false;
        const noColor = options?.noColor ?? false;
        let color = "#666";
        if (!noColor && (valueCmp as number) > 0) {
          color = isNegative ? trellisPalette[17] : trellisPalette[12];
        }
        if (!noColor && (valueCmp as number) < 0) {
          color = isNegative ? trellisPalette[12] : trellisPalette[17];
        }
        return (
          <div>
            {fmt(value)}
            <br />
            <span
              style={{
                color,
                fontSize: "1.2rem",
                fontWeight: "bold",
              }}
            >
              {fmtCmp(valueCmp)}
            </span>
          </div>
        );
      }
      return (
        <div
          className={
            options?.cellContainerClasses ?
              options.cellContainerClasses
            : `d-flex justify-content-end align-items-center h-100 ${
                options?.cellCustomClasses ? options.cellCustomClasses(id, value, props.row) : ""
              }`
          }
        >
          <span>{fmt(value)}</span>
        </div>
      );
    },
    style: { height: "60px", ...(options?.additionalStyles ?? {}) },
    filtered: true,
  };
};

export default buildMetricColumn;
